import React, { useEffect, useState, useMemo, useRef } from 'react';
import AuthUser from './AuthUser';
import toast, { Toaster } from 'react-hot-toast';
import Select from 'react-select';
import moment from 'moment';
import XLSX from 'xlsx-js-style';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getCompanyLogoUrl } from '../utils/imageUtils';

export default function GeneratedSalaryList() {
    const { http, user } = AuthUser();
    
    const [loading, setLoading] = useState(true);
    const [salaries, setSalaries] = useState([]);
    const [users, setUsers] = useState([]);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [selectedSalary, setSelectedSalary] = useState(null);
    const invoiceRef = useRef(null);
    
    // Filters
    const [filterUser, setFilterUser] = useState(null);
    const [filterMonth, setFilterMonth] = useState('');
    const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
    const [filterStatus, setFilterStatus] = useState('');

    // Company info
    const [companyName, setCompanyName] = useState('');
    const [companyLogo, setCompanyLogo] = useState('');
    const [companyAddress, setCompanyAddress] = useState('');

    // Salary heads master (for slip breakdown)
    const [masterHeads, setMasterHeads] = useState([]);

    // Edit form data
    const [editFormData, setEditFormData] = useState({
        status: '',
        remarks: '',
        payment_date: '',
        payment_mode: '',
        payment_reference: ''
    });

    useEffect(() => {
        fetchSalaries();
        fetchUsers();
        fetchCompanyInfo();
        fetchMasterHeads();
    }, [filterUser, filterMonth, filterYear, filterStatus]);

    const fetchMasterHeads = async () => {
        try {
            const response = await http.get(`/salary_master_heads?login_user=${user.id}`);
            if (response.data.success) {
                setMasterHeads(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching salary heads:', error);
        }
    };

    const fetchCompanyInfo = async () => {
        try {
            const response = await http.get(`/companyinfo?login_user=${user.id}`);
            if (response.data.data && response.data.data.length > 0) {
                setCompanyName(response.data.data[0].company_name || '');
                setCompanyLogo(response.data.data[0].company_logo || '');
                setCompanyAddress(response.data.data[0].company_address || '');
            }
        } catch (error) {
            console.error('Error fetching company info:', error);
        }
    };

    const fetchSalaries = async () => {
        setLoading(true);
        try {
            let url = `/generated_salaries?login_user=${user.id}`;
            if (filterUser) url += `&user_id=${filterUser.value}`;
            if (filterMonth) url += `&month=${filterMonth}`;
            if (filterYear) url += `&year=${filterYear}`;
            if (filterStatus) url += `&status=${filterStatus}`;
            
            const response = await http.get(url);
            if (response.data.success) {
                setSalaries(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching salaries:', error);
            toast.error('Error loading salaries');
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await http.get(`/department_user_company?login_user=${user.id}`);
            if (response.data.users) {
                setUsers(response.data.users);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const userOptions = useMemo(() => {
        return users.map(u => ({
            value: u.id,
            label: u.name + (u.last_name ? ' ' + u.last_name : '')
        }));
    }, [users]);

    const monthOptions = [
        { value: '1', label: 'Jan' }, { value: '2', label: 'Feb' }, { value: '3', label: 'Mar' },
        { value: '4', label: 'Apr' }, { value: '5', label: 'May' }, { value: '6', label: 'Jun' },
        { value: '7', label: 'Jul' }, { value: '8', label: 'Aug' }, { value: '9', label: 'Sep' },
        { value: '10', label: 'Oct' }, { value: '11', label: 'Nov' }, { value: '12', label: 'Dec' }
    ];

    const yearOptions = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let y = currentYear + 1; y >= currentYear - 5; y--) {
            years.push({ value: y.toString(), label: y.toString() });
        }
        return years;
    }, []);

    const statusOptions = [
        { value: 'generated', label: 'Generated' },
        { value: 'approved', label: 'Approved' },
        { value: 'paid', label: 'Paid' },
        { value: 'cancelled', label: 'Cancelled' }
    ];

    const paymentModeOptions = [
        { value: 'bank_transfer', label: 'Bank Transfer' },
        { value: 'cash', label: 'Cash' },
        { value: 'cheque', label: 'Cheque' },
        { value: 'upi', label: 'UPI' }
    ];

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    const getMonthName = (month) => {
        const monthNames = ['', 'January', 'February', 'March', 'April', 'May', 'June', 
                          'July', 'August', 'September', 'October', 'November', 'December'];
        return monthNames[month] || '';
    };

    const getStatusBadge = (status) => {
        const badges = {
            'generated': 'bg-info',
            'approved': 'bg-primary',
            'paid': 'bg-success',
            'cancelled': 'bg-danger'
        };
        return (
            <span className={`badge ${badges[status] || 'bg-secondary'}`} style={{ fontSize: '10px' }}>
                {status?.charAt(0).toUpperCase() + status?.slice(1)}
            </span>
        );
    };

    const handleView = (salary) => {
        setSelectedSalary(salary);
        setShowViewModal(true);
    };

    const handleEdit = (salary) => {
        setSelectedSalary(salary);
        setEditFormData({
            status: salary.status || 'generated',
            remarks: salary.remarks || '',
            payment_date: salary.payment_date ? moment(salary.payment_date).format('YYYY-MM-DD') : '',
            payment_mode: salary.payment_mode || '',
            payment_reference: salary.payment_reference || ''
        });
        setShowEditModal(true);
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await http.put(`/generated_salary/${selectedSalary.id}`, {
                ...editFormData,
                login_user: user.id
            });

            if (response.data.success) {
                toast.success('Updated successfully!');
                setShowEditModal(false);
                fetchSalaries();
            }
        } catch (error) {
            console.error('Error updating:', error);
            toast.error('Error updating record');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this salary record?')) return;

        try {
            const response = await http.delete(`/generated_salary/${id}`);
            if (response.data.success) {
                toast.success('Deleted successfully!');
                fetchSalaries();
            }
        } catch (error) {
            console.error('Error deleting:', error);
            toast.error('Error deleting record');
        }
    };

    const clearFilters = () => {
        setFilterUser(null);
        setFilterMonth('');
        setFilterYear(new Date().getFullYear().toString());
        setFilterStatus('');
    };

    const parseSalaryHeads = (jsonStr) => {
        try {
            return JSON.parse(jsonStr) || [];
        } catch {
            return [];
        }
    };

    const handleShowInvoice = (salary) => {
        setSelectedSalary(salary);
        setShowInvoiceModal(true);
    };

    const salarySlipStyles = `
        <style>
        .slip-container {
            font-family: Arial, sans-serif;
            max-width: 210mm;
            margin: 0 auto;
            padding: 25px;
            background: white;
            color: #000;
        }
        .slip-header {
            border-bottom: 3px solid #1a237e;
            padding-bottom: 15px;
            margin-bottom: 0;
        }
        .slip-company-name {
            font-size: 26px;
            font-weight: bold;
            text-transform: uppercase;
            color: #1a237e;
            margin: 5px 0;
        }
        .slip-company-address {
            font-size: 13px;
            color: #555;
        }
        .slip-title {
            background: #1a237e;
            color: #fff;
            text-align: center;
            padding: 8px;
            font-size: 16px;
            font-weight: bold;
            letter-spacing: 2px;
            margin: 0 0 15px 0;
        }
        .slip-info-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 15px;
        }
        .slip-info-table td {
            padding: 5px 10px;
            font-size: 13px;
            border: 1px solid #ddd;
        }
        .slip-info-table .label {
            font-weight: bold;
            background: #f5f5f5;
            width: 22%;
            color: #333;
        }
        .slip-info-table .value {
            width: 28%;
        }
        .slip-earn-ded-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 15px;
        }
        .slip-earn-ded-table th {
            background: #1a237e;
            color: #fff;
            padding: 8px 10px;
            font-size: 13px;
            text-align: center;
            border: 1px solid #1a237e;
        }
        .slip-earn-ded-table td {
            padding: 6px 10px;
            font-size: 12px;
            border: 1px solid #ddd;
        }
        .slip-earn-ded-table .total-row td {
            font-weight: bold;
            background: #e8eaf6;
            border-top: 2px solid #1a237e;
            font-size: 13px;
        }
        .slip-net-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
        }
        .slip-net-table td {
            padding: 10px 15px;
            border: 2px solid #1a237e;
        }
        .slip-signature {
            display: flex;
            justify-content: space-between;
            margin-top: 40px;
            padding-top: 10px;
        }
        .slip-sig-box {
            text-align: center;
            width: 200px;
        }
        .slip-sig-line {
            border-bottom: 1px solid #000;
            margin: 40px 0 8px 0;
        }
        .slip-sig-label {
            font-size: 12px;
            color: #555;
        }
        .slip-footer {
            text-align: center;
            margin-top: 20px;
            padding-top: 10px;
            border-top: 1px solid #ddd;
            font-size: 11px;
            color: #999;
            font-style: italic;
        }
        @media print {
            .slip-container {
                max-width: none;
                margin: 0;
                padding: 15px;
                box-shadow: none;
            }
            .slip-company-name { font-size: 22px; }
            .slip-title { font-size: 14px; }
            .slip-net-table td { padding: 8px 12px; }
            @page { margin: 10mm; size: A4; }
        }
        </style>
    `;

    const handlePrintInvoice = () => {
        const printContent = invoiceRef.current;
        const win = window.open('', '_blank');
        win.document.write(`
            <!DOCTYPE html>
            <html>
                <head><title>Salary Slip - ${selectedSalary?.user?.name || ''} - ${getMonthName(selectedSalary?.month)} ${selectedSalary?.year}</title>
                ${salarySlipStyles}
                </head>
                <body>${printContent.innerHTML}</body>
            </html>
        `);
        win.document.close();
        win.focus();
        win.onafterprint = () => win.close();
        win.print();
    };

    const handleSendEmail = (salary) => {
        toast.success('Email feature will be integrated soon!');
    };

    const buildExportData = () => {
        return salaries.map((s, idx) => {
            const heads = parseSalaryHeads(s.salary_heads);
            const additions = heads.filter(h => h.action === 'addition');
            const deductions = heads.filter(h => h.action === 'deduction');
            const perDay = parseFloat(s.per_day_salary) || 0;
            const workDays = parseInt(s.number_working_day) || 0;

            const row = {
                '#': idx + 1,
                'Employee': (s.user?.name || '') + (s.user?.last_name ? ' ' + s.user.last_name : ''),
                'Department': s.user?.department?.name || s.salary_master?.designation || '-',
                'Emp Type': s.salary_master?.emp_type || '-',
                'Month': getMonthName(s.month),
                'Year': s.year,
                'Basic Salary': parseFloat(s.basic_salary) || 0,
                'DA %': parseFloat(s.da_per) || 0,
                'DA Amount': parseFloat(s.da_amount) || 0,
                'HRA %': parseFloat(s.hra_per) || 0,
                'HRA Amount': parseFloat(s.hra_amount) || 0,
                'TDS %': parseFloat(s.tds_per) || 0,
                'TDS Amount': parseFloat(s.tds_amount) || 0,
                'Gross Salary': parseFloat(s.gross_salary) || 0,
                'Per Day Salary': perDay,
                'Working Days': workDays,
                'Base Amount': perDay * workDays,
            };

            additions.forEach(a => {
                row[`(+) ${a.name}`] = parseFloat(a.amount) || 0;
            });
            row['Total Additions'] = parseFloat(s.total_additions) || 0;

            deductions.forEach(d => {
                row[`(-) ${d.name}`] = parseFloat(d.amount) || 0;
            });
            row['Total Deductions'] = parseFloat(s.total_deductions) || 0;
            row['Net Salary'] = parseFloat(s.net_salary) || 0;
            row['Status'] = s.status ? s.status.charAt(0).toUpperCase() + s.status.slice(1) : '';
            row['Payment Date'] = s.payment_date ? moment(s.payment_date).format('DD-MM-YYYY') : '';
            row['Payment Mode'] = s.payment_mode ? s.payment_mode.replace('_', ' ').toUpperCase() : '';
            row['Payment Reference'] = s.payment_reference || '';
            row['Generated On'] = moment(s.created_at).format('DD-MM-YYYY');

            return row;
        });
    };

    const handleExportExcel = () => {
        if (salaries.length === 0) { toast.error('No data to export'); return; }
        const data = buildExportData();
        const keys = Object.keys(data[0]);
        const aoa = [keys, ...data.map(r => keys.map(k => r[k]))];
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        ws['!cols'] = keys.map(k => ({ wch: Math.max(k.length + 2, 14) }));

        const range = XLSX.utils.decode_range(ws['!ref']);

        for (let c = range.s.c; c <= range.e.c; c++) {
            const addr = XLSX.utils.encode_cell({ r: 0, c });
            if (ws[addr]) {
                ws[addr].s = {
                    font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
                    fill: { fgColor: { rgb: '1A237E' } },
                    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
                    border: {
                        top: { style: 'thin', color: { rgb: '1A237E' } },
                        bottom: { style: 'thin', color: { rgb: '1A237E' } },
                        left: { style: 'thin', color: { rgb: '1A237E' } },
                        right: { style: 'thin', color: { rgb: '1A237E' } }
                    }
                };
            }
        }

        for (let r = 1; r <= range.e.r; r++) {
            for (let c = range.s.c; c <= range.e.c; c++) {
                const addr = XLSX.utils.encode_cell({ r, c });
                if (!ws[addr]) continue;
                const isNum = typeof ws[addr].v === 'number';
                const isEven = r % 2 === 0;
                ws[addr].s = {
                    font: { sz: 10, color: { rgb: '333333' } },
                    fill: isEven ? { fgColor: { rgb: 'E8EAF6' } } : { fgColor: { rgb: 'FFFFFF' } },
                    alignment: { horizontal: isNum ? 'right' : 'left', vertical: 'center' },
                    border: {
                        top: { style: 'thin', color: { rgb: 'DDDDDD' } },
                        bottom: { style: 'thin', color: { rgb: 'DDDDDD' } },
                        left: { style: 'thin', color: { rgb: 'DDDDDD' } },
                        right: { style: 'thin', color: { rgb: 'DDDDDD' } }
                    },
                    ...(isNum ? { numFmt: '#,##0.00' } : {})
                };
            }
        }

        ws['!rows'] = [{ hpt: 22 }];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Generated Salaries');
        const monthLabel = filterMonth ? getMonthName(parseInt(filterMonth)) : 'All';
        const yearLabel = filterYear || 'All';
        XLSX.writeFile(wb, `Generated_Salaries_${monthLabel}_${yearLabel}.xlsx`);
        toast.success('Excel exported!');
    };

    const handleExportCSV = () => {
        if (salaries.length === 0) { toast.error('No data to export'); return; }
        const data = buildExportData();
        const ws = XLSX.utils.json_to_sheet(data);
        const csv = XLSX.utils.sheet_to_csv(ws);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const monthLabel = filterMonth ? getMonthName(parseInt(filterMonth)) : 'All';
        const yearLabel = filterYear || 'All';
        link.href = url;
        link.download = `Generated_Salaries_${monthLabel}_${yearLabel}.csv`;
        link.click();
        URL.revokeObjectURL(url);
        toast.success('CSV exported!');
    };

    const handleExportPdf = () => {
        if (salaries.length === 0) { toast.error('No data to export'); return; }
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const w = doc.internal.pageSize.getWidth();

        doc.setFontSize(16); doc.setTextColor(26, 35, 126);
        doc.text(companyName || 'Company', w / 2, 15, { align: 'center' });
        if (companyAddress) { doc.setFontSize(9); doc.setTextColor(100); doc.text(companyAddress, w / 2, 21, { align: 'center' }); }

        doc.setFillColor(26, 35, 126); doc.rect(14, 25, w - 28, 8, 'F');
        doc.setFontSize(11); doc.setTextColor(255);
        const monthLabel = filterMonth ? getMonthName(parseInt(filterMonth)) : 'All Months';
        const yearLabel = filterYear || '';
        doc.text(`GENERATED SALARIES — ${monthLabel.toUpperCase()} ${yearLabel}`, w / 2, 30.5, { align: 'center' });

        const pdfHeaders = ['#', 'Employee', 'Department', 'Type', 'Month', 'Basic', 'Gross', 'Per Day', 'Days', 'Additions', 'Deductions', 'Net Salary', 'Status'];
        const pdfRows = salaries.map((s, idx) => [
            idx + 1,
            (s.user?.name || '') + (s.user?.last_name ? ' ' + s.user.last_name : ''),
            s.user?.department?.name || '-',
            s.salary_master?.emp_type || '-',
            `${getMonthName(s.month).substring(0, 3)} ${s.year}`,
            parseFloat(s.basic_salary) || 0,
            parseFloat(s.gross_salary) || 0,
            parseFloat(s.per_day_salary) || 0,
            parseInt(s.number_working_day) || 0,
            parseFloat(s.total_additions) || 0,
            parseFloat(s.total_deductions) || 0,
            parseFloat(s.net_salary) || 0,
            s.status ? s.status.charAt(0).toUpperCase() + s.status.slice(1) : ''
        ]);

        const totNet = salaries.reduce((sum, s) => sum + (parseFloat(s.net_salary) || 0), 0);
        const totGross = salaries.reduce((sum, s) => sum + (parseFloat(s.gross_salary) || 0), 0);
        const totAdd = salaries.reduce((sum, s) => sum + (parseFloat(s.total_additions) || 0), 0);
        const totDed = salaries.reduce((sum, s) => sum + (parseFloat(s.total_deductions) || 0), 0);
        pdfRows.push(['', '', '', '', 'TOTAL', '', totGross, '', '', totAdd, totDed, totNet, '']);

        autoTable(doc, {
            head: [pdfHeaders], body: pdfRows, startY: 37, theme: 'grid',
            headStyles: { fillColor: [26, 35, 126], textColor: 255, fontSize: 7, halign: 'center', cellPadding: 2 },
            bodyStyles: { fontSize: 7, cellPadding: 1.5 },
            columnStyles: {
                0: { halign: 'center', cellWidth: 8 },
                1: { cellWidth: 30 },
                5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' },
                8: { halign: 'center' }, 9: { halign: 'right' }, 10: { halign: 'right' }, 11: { halign: 'right' },
                12: { halign: 'center' }
            },
            alternateRowStyles: { fillColor: [245, 245, 255] },
            didParseCell: (data) => {
                if (data.row.index === pdfRows.length - 1) {
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.fillColor = [232, 234, 246];
                    data.cell.styles.fontSize = 8;
                }
                if (data.section === 'body' && data.column.index === 11 && data.row.index < pdfRows.length - 1) {
                    data.cell.styles.textColor = [46, 125, 50];
                    data.cell.styles.fontStyle = 'bold';
                }
            }
        });

        doc.setFontSize(7); doc.setTextColor(150);
        doc.text(`Generated on ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}  |  ${salaries.length} records`, w / 2, doc.internal.pageSize.getHeight() - 8, { align: 'center' });

        doc.save(`Generated_Salaries_${monthLabel}_${yearLabel}.pdf`);
        toast.success('PDF exported!');
    };

    const handlePrintList = () => {
        const data = buildExportData();
        if (data.length === 0) return;
        const simpleHeaders = ['#', 'Employee', 'Department', 'Type', 'Month', 'Basic', 'Gross', 'Per Day', 'Days', 'Additions', 'Deductions', 'Net Salary', 'Status'];
        const fmt = (v) => typeof v === 'number' ? new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(v) : v;

        let tableHtml = '<table><thead><tr>' + simpleHeaders.map(h => `<th>${h}</th>`).join('') + '</tr></thead><tbody>';
        salaries.forEach((s, idx) => {
            tableHtml += `<tr${idx % 2 === 0 ? ' class="alt"' : ''}>
                <td class="center">${idx + 1}</td>
                <td><strong>${(s.user?.name || '') + (s.user?.last_name ? ' ' + s.user.last_name : '')}</strong></td>
                <td>${s.user?.department?.name || '-'}</td>
                <td>${s.salary_master?.emp_type || '-'}</td>
                <td>${getMonthName(s.month).substring(0, 3)} ${s.year}</td>
                <td class="right">${fmt(parseFloat(s.basic_salary) || 0)}</td>
                <td class="right">${fmt(parseFloat(s.gross_salary) || 0)}</td>
                <td class="right">${fmt(parseFloat(s.per_day_salary) || 0)}</td>
                <td class="center">${parseInt(s.number_working_day) || 0}</td>
                <td class="right" style="color:#2e7d32">${fmt(parseFloat(s.total_additions) || 0)}</td>
                <td class="right" style="color:#c62828">${fmt(parseFloat(s.total_deductions) || 0)}</td>
                <td class="right" style="color:#2e7d32;font-weight:bold">${fmt(parseFloat(s.net_salary) || 0)}</td>
                <td class="center">${s.status ? s.status.charAt(0).toUpperCase() + s.status.slice(1) : ''}</td>
            </tr>`;
        });
        const totNet = salaries.reduce((sum, s) => sum + (parseFloat(s.net_salary) || 0), 0);
        const totGross = salaries.reduce((sum, s) => sum + (parseFloat(s.gross_salary) || 0), 0);
        tableHtml += `<tr class="total"><td colspan="5" class="right">TOTAL</td><td></td><td class="right">${fmt(totGross)}</td><td></td><td></td><td></td><td></td><td class="right">${fmt(totNet)}</td><td></td></tr>`;
        tableHtml += '</tbody></table>';

        const monthLabel = filterMonth ? getMonthName(parseInt(filterMonth)) : 'All Months';
        const yearLabel = filterYear || '';
        const win = window.open('', '_blank');
        win.document.write(`<!DOCTYPE html><html><head><title>Generated Salaries - ${companyName}</title><style>
            body{font-family:Arial,sans-serif;margin:0;padding:20px;color:#000}
            .header{text-align:center;border-bottom:3px solid #1a237e;padding-bottom:12px;margin-bottom:5px}
            .company{font-size:22px;font-weight:bold;color:#1a237e;text-transform:uppercase}
            .addr{font-size:11px;color:#666;margin-top:3px}
            .title{background:#1a237e;color:#fff;text-align:center;padding:7px;font-size:13px;font-weight:bold;letter-spacing:2px;margin-bottom:10px}
            table{width:100%;border-collapse:collapse}
            th{background:#1a237e;color:#fff;padding:6px 8px;font-size:10px;text-align:center;border:1px solid #1a237e}
            td{padding:4px 8px;font-size:10px;border:1px solid #ddd}
            .right{text-align:right}.center{text-align:center}
            .alt{background:#f8f8ff}
            .total{font-weight:bold;background:#e8eaf6;border-top:2px solid #1a237e}
            .total td{font-size:11px}
            @media print{@page{margin:8mm;size:A4 landscape}}
        </style></head><body>
            <div class="header"><div class="company">${companyName}</div>${companyAddress ? `<div class="addr">${companyAddress}</div>` : ''}</div>
            <div class="title">GENERATED SALARIES — ${monthLabel.toUpperCase()} ${yearLabel}</div>
            ${tableHtml}
        </body></html>`);
        win.document.close(); win.focus(); win.onafterprint = () => win.close(); win.print();
    };

    // Custom styles for Select to fix z-index
    const selectStyles = {
        control: (base) => ({ ...base, minHeight: '32px', fontSize: '12px' }),
        menu: (base) => ({ ...base, zIndex: 9999 }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 })
    };

    return (
        <>
            <Toaster />
            <div className="container-fluid mt-2 px-3">
                {/* Compact Header */}
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0"><i className="fa fa-file-invoice-dollar me-2"></i>Generated Salaries</h5>
                    <div className="d-flex align-items-center gap-1">
                        <span className="badge bg-secondary me-1">{salaries.length} records</span>
                        <button className="btn btn-sm btn-outline-success py-0 px-2" onClick={handleExportExcel} disabled={salaries.length === 0} title="Export Excel">
                            <i className="fa fa-file-excel me-1"></i>Excel
                        </button>
                        <button className="btn btn-sm btn-outline-info py-0 px-2" onClick={handleExportCSV} disabled={salaries.length === 0} title="Export CSV">
                            <i className="fa fa-file-csv me-1"></i>CSV
                        </button>
                        <button className="btn btn-sm btn-outline-danger py-0 px-2" onClick={handleExportPdf} disabled={salaries.length === 0} title="Export PDF">
                            <i className="fa fa-file-pdf me-1"></i>PDF
                        </button>
                        <button className="btn btn-sm btn-outline-dark py-0 px-2" onClick={handlePrintList} disabled={salaries.length === 0} title="Print">
                            <i className="fa fa-print me-1"></i>Print
                        </button>
                    </div>
                </div>

                {/* Compact Filters */}
                <div className="card mb-2">
                    <div className="card-body py-2">
                        <div className="row g-2 align-items-end">
                            <div className="col-md-3">
                                <label className="form-label small mb-1">Employee</label>
                                <Select
                                    options={userOptions}
                                    value={filterUser}
                                    onChange={setFilterUser}
                                    isClearable
                                    placeholder="All"
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            </div>
                            <div className="col-md-2">
                                <label className="form-label small mb-1">Month</label>
                                <Select
                                    options={monthOptions}
                                    value={monthOptions.find(opt => opt.value === filterMonth)}
                                    onChange={(opt) => setFilterMonth(opt ? opt.value : '')}
                                    isClearable
                                    placeholder="All"
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            </div>
                            <div className="col-md-2">
                                <label className="form-label small mb-1">Year</label>
                                <Select
                                    options={yearOptions}
                                    value={yearOptions.find(opt => opt.value === filterYear)}
                                    onChange={(opt) => setFilterYear(opt ? opt.value : '')}
                                    isClearable
                                    placeholder="All"
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            </div>
                            <div className="col-md-2">
                                <label className="form-label small mb-1">Status</label>
                                <Select
                                    options={statusOptions}
                                    value={statusOptions.find(opt => opt.value === filterStatus)}
                                    onChange={(opt) => setFilterStatus(opt ? opt.value : '')}
                                    isClearable
                                    placeholder="All"
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            </div>
                            <div className="col-md-1">
                                <button className="btn btn-sm btn-outline-secondary w-100" onClick={clearFilters}>
                                    <i className="fa fa-times"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="card">
                    <div className="card-body p-0">
                        {loading ? (
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : salaries.length === 0 ? (
                            <div className="text-center py-4 text-muted">
                                <i className="fa fa-inbox fa-2x mb-2" style={{ opacity: 0.3 }}></i>
                                <p className="mb-0">No salary records found</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover table-sm mb-0">
                                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                                        <tr>
                                            <th>#</th>
                                            <th>Employee</th>
                                            <th>Month/Year</th>
                                            <th>Basic</th>
                                            <th>DA</th>
                                            <th>HRA</th>
                                            <th>TDS</th>
                                            <th>Gross</th>
                                            <th className="text-success">+Add</th>
                                            <th className="text-danger">-Ded</th>
                                            <th style={{ background: '#e8f4fd' }}>Net</th>
                                            <th>Status</th>
                                            <th>Generated</th>
                                            <th style={{ textAlign: 'center', minWidth: '150px' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {salaries.map((salary, index) => (
                                            <tr key={salary.id}>
                                                <td>{index + 1}</td>
                                                <td style={{ fontSize: '12px' }}>
                                                    <strong>{salary.user?.name}</strong>
                                                </td>
                                                <td style={{ fontSize: '11px' }}>
                                                    {getMonthName(salary.month).substring(0, 3)} {salary.year}
                                                </td>
                                                <td style={{ fontSize: '11px' }}>{formatCurrency(salary.basic_salary)}</td>
                                                <td style={{ fontSize: '11px' }}>{formatCurrency(salary.da_amount)}</td>
                                                <td style={{ fontSize: '11px' }}>{formatCurrency(salary.hra_amount)}</td>
                                                <td style={{ fontSize: '11px' }}>{formatCurrency(salary.tds_amount)}</td>
                                                <td style={{ fontSize: '11px' }}>{formatCurrency(salary.gross_salary)}</td>
                                                <td className="text-success" style={{ fontSize: '11px' }}>{formatCurrency(salary.total_additions)}</td>
                                                <td className="text-danger" style={{ fontSize: '11px' }}>{formatCurrency(salary.total_deductions)}</td>
                                                <td style={{ fontSize: '12px', background: '#e8f4fd' }} className="fw-bold">
                                                    {formatCurrency(salary.net_salary)}
                                                </td>
                                                <td>{getStatusBadge(salary.status)}</td>
                                                <td style={{ fontSize: '11px' }} className="text-muted">
                                                    {moment(salary.created_at).format('DD MMM YY')}
                                                </td>
                                                <td className="text-center">
                                                    <button
                                                        className="btn btn-sm btn-outline-success me-1 py-0"
                                                        onClick={() => handleShowInvoice(salary)}
                                                        title="Invoice"
                                                    >
                                                        <i className="fa fa-file-invoice"></i>
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-outline-info me-1 py-0"
                                                        onClick={() => handleView(salary)}
                                                        title="View"
                                                    >
                                                        <i className="fa fa-eye"></i>
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-outline-primary me-1 py-0"
                                                        onClick={() => handleEdit(salary)}
                                                        title="Edit"
                                                    >
                                                        <i className="fa fa-edit"></i>
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-outline-secondary me-1 py-0"
                                                        onClick={() => handleSendEmail(salary)}
                                                        title="Send Email"
                                                    >
                                                        <i className="fa fa-envelope"></i>
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-outline-danger py-0"
                                                        onClick={() => handleDelete(salary.id)}
                                                        title="Delete"
                                                    >
                                                        <i className="fa fa-trash"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Salary Slip Modal */}
            {showInvoiceModal && selectedSalary && (() => {
                const heads = parseSalaryHeads(selectedSalary.salary_heads);
                const perDay = parseFloat(selectedSalary.per_day_salary) || 0;
                const workDays = parseInt(selectedSalary.number_working_day) || 0;
                const baseAmount = perDay * workDays;
                const sm = selectedSalary.salary_master;

                const additionMasterHeads = masterHeads.filter(h => h.action === 'addition');
                const deductionMasterHeads = masterHeads.filter(h => h.action === 'deduction');

                const additionRows = additionMasterHeads.map(mh => {
                    const stored = heads.find(h => h.head_id === mh.id || h.name === mh.name);
                    return { name: mh.name, amount: stored ? parseFloat(stored.amount) || 0 : 0 };
                });
                heads.filter(h => h.action === 'addition' && !h.is_statutory).forEach(h => {
                    if (!additionRows.find(r => r.name === h.name)) {
                        additionRows.push({ name: h.name, amount: parseFloat(h.amount) || 0 });
                    }
                });

                const otherDeductionRows = deductionMasterHeads.map(mh => {
                    const stored = heads.find(h => (h.head_id === mh.id || h.name === mh.name) && !h.is_statutory);
                    return { name: mh.name, amount: stored ? parseFloat(stored.amount) || 0 : 0 };
                });
                heads.filter(h => h.action === 'deduction' && !h.is_statutory).forEach(h => {
                    if (!otherDeductionRows.find(r => r.name === h.name)) {
                        otherDeductionRows.push({ name: h.name, amount: parseFloat(h.amount) || 0 });
                    }
                });

                let statutoryRows = heads.filter(h => h.is_statutory).map(h => ({ name: h.name, amount: parseFloat(h.amount) || 0 }));
                if (statutoryRows.length === 0 && sm) {
                    const masterDays = parseInt(sm.working_days) || 30;
                    const pfEmp = parseFloat(sm.total_pf_employee) || 0;
                    const esiEmp = parseFloat(sm.total_esi_employee) || 0;
                    const pt = (sm.pt_enabled && parseFloat(sm.total_pt)) ? parseFloat(sm.total_pt) : 0;
                    const tds = parseFloat(sm.total_tds_deduction) || 0;
                    if (pfEmp > 0) statutoryRows.push({ name: 'PF (Employee)', amount: (pfEmp / masterDays) * workDays });
                    if (esiEmp > 0) statutoryRows.push({ name: 'ESI (Employee)', amount: (esiEmp / masterDays) * workDays });
                    if (pt > 0) statutoryRows.push({ name: 'Professional Tax', amount: (pt / masterDays) * workDays });
                    if (tds > 0) statutoryRows.push({ name: 'TDS', amount: (tds / masterDays) * workDays });
                }

                const totalAdditions = additionRows.reduce((s, h) => s + h.amount, 0);
                const totalStatutory = statutoryRows.reduce((s, h) => s + h.amount, 0);
                const totalOtherDed = otherDeductionRows.reduce((s, h) => s + h.amount, 0);
                const totalEarnings = baseAmount + totalAdditions;
                const totalDeductions = totalStatutory + totalOtherDed;

                return (
                <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                        <div className="modal-content" style={{ border: 'none', borderRadius: '8px' }}>
                            <div className="modal-header py-2" style={{ background: '#1a237e', color: '#fff' }}>
                                <h6 className="modal-title">
                                    <i className="fa fa-file-invoice me-2"></i>Salary Slip - {getMonthName(selectedSalary.month)} {selectedSalary.year}
                                </h6>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowInvoiceModal(false)}></button>
                            </div>
                            <div className="modal-body p-0" style={{ background: '#f5f5f5' }}>
                                <div ref={invoiceRef}>
                                    <div className="slip-container" style={{ fontFamily: 'Arial, sans-serif', maxWidth: '210mm', margin: '0 auto', padding: '25px', background: '#fff', color: '#000' }}>

                                        {/* Header with Logo */}
                                        <div style={{ borderBottom: '3px solid #1a237e', paddingBottom: '15px', marginBottom: '0' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <div style={{ flex: '0 0 100px' }}>
                                                    {companyLogo && (
                                                        <img
                                                            src={getCompanyLogoUrl(companyLogo)}
                                                            alt="Logo"
                                                            style={{ width: '90px', height: '70px', objectFit: 'contain' }}
                                                        />
                                                    )}
                                                </div>
                                                <div style={{ flex: '1', textAlign: 'center' }}>
                                                    <div style={{ fontSize: '26px', fontWeight: 'bold', textTransform: 'uppercase', color: '#1a237e', margin: '5px 0' }}>
                                                        {companyName}
                                                    </div>
                                                    {companyAddress && (
                                                        <div style={{ fontSize: '13px', color: '#555' }}>{companyAddress}</div>
                                                    )}
                                                </div>
                                                <div style={{ flex: '0 0 100px' }}></div>
                                            </div>
                                        </div>

                                        {/* Title Bar */}
                                        <div style={{ background: '#1a237e', color: '#fff', textAlign: 'center', padding: '8px', fontSize: '16px', fontWeight: 'bold', letterSpacing: '2px', margin: '0 0 15px 0' }}>
                                            SALARY SLIP — {getMonthName(selectedSalary.month).toUpperCase()} {selectedSalary.year}
                                        </div>

                                        {/* Employee Info Grid */}
                                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '15px' }}>
                                            <tbody>
                                                <tr>
                                                    <td style={{ padding: '5px 10px', fontSize: '13px', border: '1px solid #ddd', fontWeight: 'bold', background: '#f5f5f5', width: '22%' }}>Employee Name</td>
                                                    <td style={{ padding: '5px 10px', fontSize: '13px', border: '1px solid #ddd', width: '28%' }}>{selectedSalary.user?.name} {selectedSalary.user?.last_name || ''}</td>
                                                    <td style={{ padding: '5px 10px', fontSize: '13px', border: '1px solid #ddd', fontWeight: 'bold', background: '#f5f5f5', width: '22%' }}>Pay Period</td>
                                                    <td style={{ padding: '5px 10px', fontSize: '13px', border: '1px solid #ddd', width: '28%' }}>{getMonthName(selectedSalary.month)} {selectedSalary.year}</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ padding: '5px 10px', fontSize: '13px', border: '1px solid #ddd', fontWeight: 'bold', background: '#f5f5f5' }}>Department</td>
                                                    <td style={{ padding: '5px 10px', fontSize: '13px', border: '1px solid #ddd' }}>{selectedSalary.user?.department?.name || sm?.designation || '-'}</td>
                                                    <td style={{ padding: '5px 10px', fontSize: '13px', border: '1px solid #ddd', fontWeight: 'bold', background: '#f5f5f5' }}>Working Days</td>
                                                    <td style={{ padding: '5px 10px', fontSize: '13px', border: '1px solid #ddd' }}>{workDays}</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ padding: '5px 10px', fontSize: '13px', border: '1px solid #ddd', fontWeight: 'bold', background: '#f5f5f5' }}>Designation</td>
                                                    <td style={{ padding: '5px 10px', fontSize: '13px', border: '1px solid #ddd' }}>{sm?.designation || '-'}</td>
                                                    <td style={{ padding: '5px 10px', fontSize: '13px', border: '1px solid #ddd', fontWeight: 'bold', background: '#f5f5f5' }}>Per Day Salary</td>
                                                    <td style={{ padding: '5px 10px', fontSize: '13px', border: '1px solid #ddd' }}>{formatCurrency(perDay)}</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ padding: '5px 10px', fontSize: '13px', border: '1px solid #ddd', fontWeight: 'bold', background: '#f5f5f5' }}>Emp Type</td>
                                                    <td style={{ padding: '5px 10px', fontSize: '13px', border: '1px solid #ddd' }}>{sm?.emp_type || '-'}</td>
                                                    <td style={{ padding: '5px 10px', fontSize: '13px', border: '1px solid #ddd', fontWeight: 'bold', background: '#f5f5f5' }}>Bank A/C</td>
                                                    <td style={{ padding: '5px 10px', fontSize: '13px', border: '1px solid #ddd' }}>{sm?.account_num || '-'}</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ padding: '5px 10px', fontSize: '13px', border: '1px solid #ddd', fontWeight: 'bold', background: '#f5f5f5' }}>Date of Joining</td>
                                                    <td style={{ padding: '5px 10px', fontSize: '13px', border: '1px solid #ddd' }}>{sm?.date_of_join ? moment(sm.date_of_join).format('DD MMM YYYY') : '-'}</td>
                                                    <td style={{ padding: '5px 10px', fontSize: '13px', border: '1px solid #ddd', fontWeight: 'bold', background: '#f5f5f5' }}>PF A/C No</td>
                                                    <td style={{ padding: '5px 10px', fontSize: '13px', border: '1px solid #ddd' }}>{sm?.pf_acc_num || '-'}</td>
                                                </tr>
                                            </tbody>
                                        </table>

                                        {/* Earnings Section */}
                                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
                                            <thead>
                                                <tr>
                                                    <th colSpan="2" style={{ background: '#2e7d32', color: '#fff', padding: '7px 10px', fontSize: '13px', textAlign: 'center', border: '1px solid #2e7d32' }}>
                                                        <i className="fa fa-plus-circle" style={{ marginRight: '6px' }}></i>Earnings / Additions
                                                    </th>
                                                </tr>
                                                <tr>
                                                    <th style={{ background: '#e8f5e9', padding: '6px 10px', fontSize: '12px', border: '1px solid #ddd', width: '70%' }}>Particulars</th>
                                                    <th style={{ background: '#e8f5e9', padding: '6px 10px', fontSize: '12px', border: '1px solid #ddd', textAlign: 'right', width: '30%' }}>Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid #ddd' }}>
                                                        Base Salary <span style={{ color: '#888', fontSize: '11px' }}>({formatCurrency(perDay)} × {workDays} days)</span>
                                                    </td>
                                                    <td style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid #ddd', textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(baseAmount)}</td>
                                                </tr>
                                                {additionRows.map((item, idx) => (
                                                    <tr key={`add-${idx}`}>
                                                        <td style={{ padding: '5px 10px', fontSize: '12px', border: '1px solid #ddd' }}>{item.name}</td>
                                                        <td style={{ padding: '5px 10px', fontSize: '12px', border: '1px solid #ddd', textAlign: 'right', color: item.amount > 0 ? '#2e7d32' : '#999' }}>
                                                            {item.amount > 0 ? formatCurrency(item.amount) : '0.00'}
                                                        </td>
                                                    </tr>
                                                ))}
                                                <tr>
                                                    <td style={{ padding: '8px 10px', fontSize: '13px', border: '1px solid #ddd', fontWeight: 'bold', background: '#e8f5e9', borderTop: '2px solid #2e7d32' }}>Total Earnings</td>
                                                    <td style={{ padding: '8px 10px', fontSize: '13px', border: '1px solid #ddd', fontWeight: 'bold', background: '#e8f5e9', borderTop: '2px solid #2e7d32', textAlign: 'right', color: '#2e7d32' }}>
                                                        {formatCurrency(totalEarnings)}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>

                                        {/* Deductions Section */}
                                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '15px' }}>
                                            <thead>
                                                <tr>
                                                    <th colSpan="2" style={{ background: '#c62828', color: '#fff', padding: '7px 10px', fontSize: '13px', textAlign: 'center', border: '1px solid #c62828' }}>
                                                        <i className="fa fa-minus-circle" style={{ marginRight: '6px' }}></i>Deductions
                                                    </th>
                                                </tr>
                                                <tr>
                                                    <th style={{ background: '#ffebee', padding: '6px 10px', fontSize: '12px', border: '1px solid #ddd', width: '70%' }}>Particulars</th>
                                                    <th style={{ background: '#ffebee', padding: '6px 10px', fontSize: '12px', border: '1px solid #ddd', textAlign: 'right', width: '30%' }}>Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {statutoryRows.length > 0 && (
                                                    <tr>
                                                        <td colSpan="2" style={{ padding: '5px 10px', fontSize: '11px', fontWeight: 'bold', background: '#fff3e0', color: '#e65100', border: '1px solid #ddd', letterSpacing: '0.5px' }}>
                                                            <i className="fa fa-landmark" style={{ marginRight: '5px', fontSize: '10px' }}></i>Statutory Deductions
                                                        </td>
                                                    </tr>
                                                )}
                                                {statutoryRows.map((item, idx) => (
                                                    <tr key={`stat-${idx}`}>
                                                        <td style={{ padding: '5px 10px', fontSize: '12px', border: '1px solid #ddd', paddingLeft: '20px' }}>{item.name}</td>
                                                        <td style={{ padding: '5px 10px', fontSize: '12px', border: '1px solid #ddd', textAlign: 'right', color: item.amount > 0 ? '#c62828' : '#999' }}>
                                                            {item.amount > 0 ? formatCurrency(item.amount) : '0.00'}
                                                        </td>
                                                    </tr>
                                                ))}
                                                {otherDeductionRows.length > 0 && (
                                                    <tr>
                                                        <td colSpan="2" style={{ padding: '5px 10px', fontSize: '11px', fontWeight: 'bold', background: '#fce4ec', color: '#c62828', border: '1px solid #ddd', letterSpacing: '0.5px' }}>
                                                            <i className="fa fa-file-invoice" style={{ marginRight: '5px', fontSize: '10px' }}></i>Other Deductions
                                                        </td>
                                                    </tr>
                                                )}
                                                {otherDeductionRows.map((item, idx) => (
                                                    <tr key={`ded-${idx}`}>
                                                        <td style={{ padding: '5px 10px', fontSize: '12px', border: '1px solid #ddd', paddingLeft: '20px' }}>{item.name}</td>
                                                        <td style={{ padding: '5px 10px', fontSize: '12px', border: '1px solid #ddd', textAlign: 'right', color: item.amount > 0 ? '#c62828' : '#999' }}>
                                                            {item.amount > 0 ? formatCurrency(item.amount) : '0.00'}
                                                        </td>
                                                    </tr>
                                                ))}
                                                <tr>
                                                    <td style={{ padding: '8px 10px', fontSize: '13px', border: '1px solid #ddd', fontWeight: 'bold', background: '#ffebee', borderTop: '2px solid #c62828' }}>Total Deductions</td>
                                                    <td style={{ padding: '8px 10px', fontSize: '13px', border: '1px solid #ddd', fontWeight: 'bold', background: '#ffebee', borderTop: '2px solid #c62828', textAlign: 'right', color: '#c62828' }}>
                                                        {formatCurrency(totalDeductions || selectedSalary.total_deductions)}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>

                                        {/* Net Salary Row */}
                                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                                            <tbody>
                                                <tr>
                                                    <td style={{ padding: '10px 15px', fontSize: '14px', fontWeight: 'bold', border: '2px solid #1a237e', background: '#f5f5f5' }}>
                                                        Net Salary Payable
                                                    </td>
                                                    <td style={{ padding: '10px 15px', fontSize: '18px', fontWeight: 'bold', border: '2px solid #1a237e', textAlign: 'right', color: '#1a237e' }}>
                                                        {formatCurrency(selectedSalary.net_salary)}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>

                                        {/* Payment Info (if paid) */}
                                        {selectedSalary.payment_date && (
                                            <div style={{ background: '#e8f5e9', border: '1px solid #c8e6c9', borderRadius: '4px', padding: '10px 15px', marginBottom: '15px', fontSize: '12px' }}>
                                                <strong>Payment Info:</strong>&nbsp;
                                                Paid on {moment(selectedSalary.payment_date).format('DD MMM YYYY')}
                                                {selectedSalary.payment_mode && <>&nbsp;via <strong>{selectedSalary.payment_mode.replace('_', ' ').toUpperCase()}</strong></>}
                                                {selectedSalary.payment_reference && <>&nbsp;(Ref: {selectedSalary.payment_reference})</>}
                                            </div>
                                        )}

                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer py-2" style={{ background: '#f5f5f5' }}>
                                <button type="button" className="btn btn-sm btn-success" onClick={handlePrintInvoice}>
                                    <i className="fa fa-print me-1"></i>Print Salary Slip
                                </button>
                                <button type="button" className="btn btn-sm btn-secondary" onClick={() => setShowInvoiceModal(false)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                );
            })()}

            {/* View Modal */}
            {showViewModal && selectedSalary && (
                <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-header" style={{ background: '#f8f9fa' }}>
                                <h6 className="modal-title">
                                    <i className="fa fa-file-invoice me-2"></i>
                                    Salary Details - {getMonthName(selectedSalary.month)} {selectedSalary.year}
                                </h6>
                                <button type="button" className="btn-close" onClick={() => setShowViewModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                {/* Employee Info */}
                                <div className="card mb-3">
                                    <div className="card-header bg-light py-2">
                                        <h6 className="mb-0"><i className="fa fa-user me-2"></i>Employee</h6>
                                    </div>
                                    <div className="card-body py-2">
                                        <div className="row">
                                            <div className="col-md-6">
                                                <p className="mb-1"><strong>Name:</strong> {selectedSalary.user?.name} {selectedSalary.user?.last_name || ''}</p>
                                                <p className="mb-0"><strong>Period:</strong> {getMonthName(selectedSalary.month)} {selectedSalary.year}</p>
                                            </div>
                                            <div className="col-md-6">
                                                <p className="mb-1"><strong>Status:</strong> {getStatusBadge(selectedSalary.status)}</p>
                                                <p className="mb-0"><strong>Generated:</strong> {moment(selectedSalary.created_at).format('DD MMM YYYY')}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Salary Components Table */}
                                <div className="card mb-3">
                                    <div className="card-header bg-light py-2">
                                        <h6 className="mb-0"><i className="fa fa-money-bill me-2"></i>Salary Breakdown</h6>
                                    </div>
                                    <div className="card-body p-0">
                                        <table className="table table-sm mb-0">
                                            <tbody>
                                                <tr>
                                                    <td>Basic Salary</td>
                                                    <td className="text-end">{formatCurrency(selectedSalary.basic_salary)}</td>
                                                </tr>
                                                <tr>
                                                    <td>DA ({selectedSalary.da_per}%)</td>
                                                    <td className="text-end">{formatCurrency(selectedSalary.da_amount)}</td>
                                                </tr>
                                                <tr>
                                                    <td>HRA ({selectedSalary.hra_per}%)</td>
                                                    <td className="text-end">{formatCurrency(selectedSalary.hra_amount)}</td>
                                                </tr>
                                                <tr>
                                                    <td>TDS ({selectedSalary.tds_per}%)</td>
                                                    <td className="text-end text-danger">-{formatCurrency(selectedSalary.tds_amount)}</td>
                                                </tr>
                                                <tr style={{ background: '#f8f9fa' }}>
                                                    <td className="fw-semibold">Gross Salary</td>
                                                    <td className="text-end fw-semibold">{formatCurrency(selectedSalary.gross_salary)}</td>
                                                </tr>
                                                <tr>
                                                    <td className="text-success">+ Additions</td>
                                                    <td className="text-end text-success">+{formatCurrency(selectedSalary.total_additions)}</td>
                                                </tr>
                                                <tr>
                                                    <td className="text-danger">- Deductions</td>
                                                    <td className="text-end text-danger">-{formatCurrency(selectedSalary.total_deductions)}</td>
                                                </tr>
                                                <tr style={{ background: '#e8f4fd' }}>
                                                    <td className="fw-bold fs-6">Net Salary</td>
                                                    <td className="text-end fw-bold fs-6">{formatCurrency(selectedSalary.net_salary)}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Salary Heads */}
                                {parseSalaryHeads(selectedSalary.salary_heads).length > 0 && (
                                    <div className="card">
                                        <div className="card-header bg-light py-2">
                                            <h6 className="mb-0"><i className="fa fa-list me-2"></i>Additions & Deductions</h6>
                                        </div>
                                        <div className="card-body p-0">
                                            <table className="table table-sm mb-0">
                                                <thead style={{ backgroundColor: '#f8f9fa' }}>
                                                    <tr>
                                                        <th>Name</th>
                                                        <th>Type</th>
                                                        <th className="text-end">Amount</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {parseSalaryHeads(selectedSalary.salary_heads).map((head, idx) => (
                                                        <tr key={idx}>
                                                            <td>{head.name}</td>
                                                            <td>
                                                                {head.action === 'addition' ? (
                                                                    <span className="badge bg-success" style={{ fontSize: '10px' }}>Addition</span>
                                                                ) : (
                                                                    <span className="badge bg-danger" style={{ fontSize: '10px' }}>Deduction</span>
                                                                )}
                                                            </td>
                                                            <td className="text-end">{formatCurrency(head.amount)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer py-2">
                                <button type="button" className="btn btn-sm btn-secondary" onClick={() => setShowViewModal(false)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedSalary && (
                <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header" style={{ background: '#f8f9fa' }}>
                                <h6 className="modal-title">
                                    <i className="fa fa-edit me-2"></i>Edit Salary Record
                                </h6>
                                <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
                            </div>
                            <form onSubmit={handleEditSubmit}>
                                <div className="modal-body">
                                    <div className="alert alert-info py-2 mb-3">
                                        <strong>{selectedSalary.user?.name}</strong> - {getMonthName(selectedSalary.month)} {selectedSalary.year}
                                        <br />
                                        <small>Net Salary: {formatCurrency(selectedSalary.net_salary)}</small>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Status</label>
                                        <Select
                                            options={statusOptions}
                                            value={statusOptions.find(opt => opt.value === editFormData.status)}
                                            onChange={(opt) => setEditFormData(prev => ({ ...prev, status: opt ? opt.value : 'generated' }))}
                                            styles={selectStyles}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                        />
                                    </div>

                                    {editFormData.status === 'paid' && (
                                        <>
                                            <div className="mb-3">
                                                <label className="form-label">Payment Date</label>
                                                <input
                                                    type="date"
                                                    className="form-control form-control-sm"
                                                    value={editFormData.payment_date}
                                                    onChange={(e) => setEditFormData(prev => ({ ...prev, payment_date: e.target.value }))}
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Payment Mode</label>
                                                <Select
                                                    options={paymentModeOptions}
                                                    value={paymentModeOptions.find(opt => opt.value === editFormData.payment_mode)}
                                                    onChange={(opt) => setEditFormData(prev => ({ ...prev, payment_mode: opt ? opt.value : '' }))}
                                                    isClearable
                                                    placeholder="Select mode..."
                                                    styles={selectStyles}
                                                    menuPortalTarget={document.body}
                                                    menuPosition="fixed"
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Payment Reference</label>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    value={editFormData.payment_reference}
                                                    onChange={(e) => setEditFormData(prev => ({ ...prev, payment_reference: e.target.value }))}
                                                    placeholder="Transaction ID / Cheque No."
                                                />
                                            </div>
                                        </>
                                    )}

                                    <div className="mb-3">
                                        <label className="form-label">Remarks</label>
                                        <textarea
                                            className="form-control form-control-sm"
                                            rows="2"
                                            value={editFormData.remarks}
                                            onChange={(e) => setEditFormData(prev => ({ ...prev, remarks: e.target.value }))}
                                            placeholder="Optional notes..."
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer py-2">
                                    <button type="button" className="btn btn-sm btn-secondary" onClick={() => setShowEditModal(false)}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-sm btn-primary">
                                        <i className="fa fa-save me-1"></i>Update
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
